import { debugLog, debugWarn } from './store-layer/debugUtils';
// Core interfaces for the SVG Map components

export interface Store {
  id: string;
  name: string;
  floorIds: number[];
  pathData?: string;
  bbox?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  services?: Feature[];
  spaceCode?: string;
  storeData?: any;
  originalId?: string;
}

export interface Feature {
  type: string;
  x: number;
  y: number;
}

export interface Floor {
  id: number;
  name: string | {languageCode: string, text: string}[];
  order: number;
  floorMaps?: FloorMap[];
}

export interface FloorMap {
  type?: string;
  url?: string;
  content?: string;
}

export interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Clean and optimize SVG content
 */
export function preprocessSvg(svgContent: string): string {
  if (!svgContent) return '';
  
  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
      return svgContent; // Return unmodified content in SSR mode
    }
    
    // Create a DOM parser and parse the SVG content
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    
    // Check for parsing errors
    const parserError = svgDoc.querySelector('parsererror');
    if (parserError) {
      console.error('SVG parsing error:', parserError.textContent);
      return svgContent; // Return original if parsing failed
    }
    
    // Get the SVG element
    const svgElement = svgDoc.querySelector('svg');
    if (!svgElement) {
      console.error('No SVG element found in content');
      return svgContent;
    }
    
    // Try to find the main content layer - attempt multiple possible layer IDs
    const possibleLayerIds = ['Layer_1', 'layer_1', 'Layer1', 'layer1', 'content', 'map-content', 'mapContent'];
    let mainContentLayer: Element | null = null;
    
    // Check each possible layer ID
    for (const layerId of possibleLayerIds) {
      const layer = svgDoc.querySelector(`#${layerId}`);
      if (layer) {
        mainContentLayer = layer;
        debugLog(`Found content layer with ID: #${layerId}`, 'svgUpdates');
        break;
      }
    }
    
    // If no named layers were found, look for the largest group that might be the content layer
    if (!mainContentLayer) {
      const groups = Array.from(svgDoc.querySelectorAll('g'));
      if (groups.length > 0) {
        // Find the group with the most children, which is likely the main content
        let maxChildren = 0;
        let largestGroup: Element | null = null;
        
        groups.forEach(group => {
          const childCount = group.childElementCount;
          if (childCount > maxChildren) {
            maxChildren = childCount;
            largestGroup = group;
          }
        });
        
        if (largestGroup) {
          mainContentLayer = largestGroup;
          debugLog(`No named layer found. Using largest group with ${maxChildren} children as content layer.`, 'svgUpdates');
        }
      }
    }
    
    // Disable pointer events on the main content layer if found
    if (mainContentLayer) {
      debugLog('Disabling pointer events on main content layer', 'svgUpdates');
      (mainContentLayer as SVGElement).setAttribute('style', 'pointer-events: none;');
    } else {
      // If we couldn't find a main layer, just log a warning
      debugWarn('Could not identify main content layer in SVG - interactive elements may not work correctly');
    }
    
    // Store original dimensions if needed for viewBox
    let width = '1000', height = '1000';
    if (svgElement.hasAttribute('width')) {
      width = svgElement.getAttribute('width') || '1000';
      // Extract numeric part if it contains units like px, em, etc.
      width = width.replace(/[^0-9.]/g, '');
    }
    
    if (svgElement.hasAttribute('height')) {
      height = svgElement.getAttribute('height') || '1000';
      // Extract numeric part if it contains units like px, em, etc.
      height = height.replace(/[^0-9.]/g, '');
    }
    
    // Add viewBox if it doesn't exist
    if (!svgElement.hasAttribute('viewBox')) {
      svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
    }
    
    // Set preserveAspectRatio attribute to ensure proper scaling
    svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    
    // Make SVG responsive by setting percentage dimensions
    svgElement.setAttribute('width', '100%');
    svgElement.setAttribute('height', '100%');
    
    // Return the serialized SVG
    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgElement);
  } catch (error) {
    console.error('Error processing SVG:', error);
    return svgContent; // Return original on error
  }
}

/**
 * Extract store data from SVG
 */
export function extractStores(svgContent: string): Store[] {
  if (!svgContent) return [];
  
  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
      return []; // Return empty array in SSR mode
    }
    
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    
    // Log SVG structure for debugging
    const svgElement = svgDoc.querySelector('svg');
    debugLog('SVG root ID: ' + (svgElement?.id || 'none'), 'svgUpdates');
    
    // Find all elements with IDs to see what's available
    const elementIds = Array.from(svgDoc.querySelectorAll('[id]'))
      .map(el => ({ id: (el as SVGElement).id, tag: el.tagName }));
    debugLog(`Found ${elementIds.length} elements with IDs`, 'svgUpdates');
    
    // Look for possible layer elements - try multiple common IDs
    const possibleLayerIds = ['Layer_1', 'layer_1', 'Layer1', 'layer1', 'content', 'map-content', 'mapContent'];
    let contentLayer: Element | null = null;
    
    // Check each possible layer ID
    for (const layerId of possibleLayerIds) {
      const layer = svgDoc.querySelector(`#${layerId}`);
      if (layer) {
        contentLayer = layer;
        debugLog(`Found content layer with ID: #${layerId}`, 'svgUpdates');
        
        // Log content layer children to see if store elements are inside
        const layerChildren = Array.from(layer.children).map(child => ({
          id: (child as SVGElement).id || 'no-id',
          tag: child.tagName
        }));
        debugLog(`Content layer has ${layerChildren.length} children`, 'svgUpdates');
        
        break;
      }
    }
    
    // If no standard layer was found, try to find the largest group
    if (!contentLayer) {
      const groups = Array.from(svgDoc.querySelectorAll('g'));
      if (groups.length > 0) {
        // Find the group with the most children, which is likely the main content
        let maxChildren = 0;
        let largestGroup: Element | null = null;
        
        groups.forEach(group => {
          const childCount = group.childElementCount;
          if (childCount > maxChildren) {
            maxChildren = childCount;
            largestGroup = group;
          }
        });
        
        if (largestGroup) {
          contentLayer = largestGroup;
          debugLog(`No named layer found. Using largest group with ${maxChildren} children as content layer.`, 'svgUpdates');
        }
      }
    }
    
    // Get all potential store elements - paths, rects, etc. with IDs
    // Try to exclude structural elements like layers
    const potentialStores = Array.from(svgDoc.querySelectorAll('path[id], rect[id], polygon[id]'))
      .filter(el => {
        const id = (el as SVGElement).id;
        // Skip layer elements and elements with very short IDs (likely not stores)
        return id && 
               !possibleLayerIds.includes(id) && 
               !id.startsWith('layer') && 
               id.length > 1;
      });
    
    debugLog(`Found ${potentialStores.length} potential store elements`, 'svgUpdates');
    
    // If no direct elements found and we have a content layer, look inside it
    if (potentialStores.length === 0 && contentLayer) {
      const layerStores = Array.from(contentLayer.querySelectorAll('path[id], rect[id], polygon[id]'))
        .filter(el => {
          const id = (el as SVGElement).id;
          return id && 
                 !possibleLayerIds.includes(id) && 
                 !id.startsWith('layer') && 
                 id.length > 1;
        });
      debugLog(`Found ${layerStores.length} store elements inside content layer`, 'svgUpdates');
      potentialStores.push(...layerStores);
    }
    
    // If we still don't have any store elements, try looking for elements with store-related class names
    if (potentialStores.length === 0) {
      const classBasedStores = Array.from(svgDoc.querySelectorAll('.store, .shop, .unit, .space, .tenant, [class*="store"], [class*="shop"]'));
      debugLog(`Found ${classBasedStores.length} elements with store-related classes`, 'svgUpdates');
      potentialStores.push(...classBasedStores);
    }
    
    return potentialStores.map((element, index) => {
      const el = element as SVGElement;
      // Use ID if available, otherwise generate a sequential ID
      const id = el.id || `store-${index + 1}`;
      
      // Calculate bounding box
      let bbox = { x: 0, y: 0, width: 0, height: 0 };
      try {
        if (el.tagName.toLowerCase() === 'path') {
          const pathData = el.getAttribute('d');
          if (pathData) {
            bbox = calculatePathBBox(pathData);
          }
        } else {
          const x = parseFloat(el.getAttribute('x') || '0');
          const y = parseFloat(el.getAttribute('y') || '0');
          const width = parseFloat(el.getAttribute('width') || '0');
          const height = parseFloat(el.getAttribute('height') || '0');
          
          if (width > 0 && height > 0) {
            bbox = { x, y, width, height };
          }
        }
      } catch (e) {
        console.warn('Error calculating bbox for', id, e);
      }
      
      // Clean up the ID to get the spaceCode
      // Try to find the actual store identifier
      let spaceCode = id;
      
      // Look for data-space-code or data-store-code attributes first
      const dataSpaceCode = el.getAttribute('data-space-code') || el.getAttribute('data-store-code');
      if (dataSpaceCode) {
        spaceCode = dataSpaceCode;
      } 
      // Otherwise try to extract from ID
      else if (id.includes('-')) {
        const parts = id.split('-');
        if (parts.length > 1 && 
            (parts[0] === 'store' || 
             parts[0] === 'storePath' || 
             parts[0] === 'shop' || 
             parts[0] === 'unit' || 
             parts[0] === 'space')) {
          spaceCode = parts.slice(1).join('-');
        }
      }
      
      debugLog(`Extracted store with ID: ${id}, spaceCode: ${spaceCode}`, 'svgUpdates');
      
      // Try to get store name from various attributes
      const name = el.getAttribute('data-name') || 
                   el.getAttribute('data-store-name') || 
                   el.getAttribute('data-label') ||
                   spaceCode;
      
      return {
        id: spaceCode,
        name: name,
        floorIds: [],
        bbox,
        spaceCode,
        originalId: id
      } as Store;
    });
  } catch (error) {
    console.error('Error extracting stores from SVG:', error);
    return [];
  }
}

/**
 * Calculate bounding box from SVG path data
 */
function calculatePathBBox(pathData: string): { x: number, y: number, width: number, height: number } {
  try {
    // Basic parsing of path data to find min/max coordinates
    // This is a simplified version and may not handle all path commands correctly
    
    // Remove commands and extract coordinate pairs
    const coordinatePattern = /(-?\d+\.?\d*)[,\s](-?\d+\.?\d*)/g;
    const matches = [...pathData.matchAll(coordinatePattern)];
    
    if (matches.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    
    // Find min and max coordinates
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    
    matches.forEach(match => {
      const x = parseFloat(match[1]);
      const y = parseFloat(match[2]);
      
      if (!isNaN(x) && !isNaN(y)) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    });
    
    // Calculate width and height
    const width = maxX - minX;
    const height = maxY - minY;
    
    return {
      x: minX,
      y: minY,
      width: width > 0 ? width : 1, // Ensure positive width
      height: height > 0 ? height : 1 // Ensure positive height
    };
  } catch (e) {
    debugWarn('Error calculating path bounding box:', e);
    return { x: 0, y: 0, width: 0, height: 0 };
  }
}

/**
 * Parse floor IDs from data attribute
 */
function parseFloorIds(floorsAttr: string): number[] {
  if (!floorsAttr) return [];
  
  try {
    // Try parsing as JSON
    return JSON.parse(floorsAttr);
  } catch {
    // Fallback to comma-separated list
    return floorsAttr.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
  }
}

/**
 * Parse service data from data attribute
 */
function parseServices(servicesAttr: string): Feature[] {
  if (!servicesAttr) return [];
  
  try {
    return JSON.parse(servicesAttr);
  } catch {
    return [];
  }
} 